import { ref } from 'vue'
import axios from 'axios'

// Global state for currency symbol
const currencySymbol = ref('€')
const isLoaded = ref(false)

/**
 * Composable for currency formatting with dynamic symbol
 */
export function useCurrency() {
    /**
     * Load currency symbol from backend
     */
    const loadCurrencySymbol = async () => {
        if (isLoaded.value) return

        try {
            const response = await axios.get('/api/general-settings')
            if (response.data.success && response.data.data.currency_symbol) {
                currencySymbol.value = response.data.data.currency_symbol
            }
            isLoaded.value = true
        } catch (error) {
            // Silence 401 errors as they are expected when user is not logged in
            if (error.response && error.response.status === 401) {
                currencySymbol.value = '€'
                return
            }
            // console.warn('Failed to load currency symbol, using default:', error)
            // Do not set isLoaded = true on error, so we can retry later (e.g. after login)
            currencySymbol.value = '€'
        }
    }

    /**
     * Format amount with currency symbol
     * @param {number} amount - The amount to format
     * @param {boolean} showSymbol - Whether to show the currency symbol
     * @returns {string} Formatted currency string
     */
    const formatCurrency = (amount, showSymbol = true, decimals = 2) => {
        const formatted = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(amount || 0)

        return showSymbol ? `${formatted} ${currencySymbol.value}` : formatted
    }

    /**
     * Get the current currency symbol
     * @returns {string} Current currency symbol
     */
    const getCurrencySymbol = () => {
        return currencySymbol.value
    }

    /**
     * Set currency symbol (useful after updating settings)
     * @param {string} symbol - New currency symbol
     */
    const setCurrencySymbol = (symbol) => {
        currencySymbol.value = symbol
    }

    /**
     * Format amount with compact notation (K, M, Md) for large numbers
     * e.g. 496936600000 -> "496.9 Md Xof", 51315400000 -> "51.3 Md Xof", 2500000 -> "2.5 M Xof"
     * @param {number} amount - The amount to format
     * @returns {string} Formatted compact currency string
     */
    const compactUnits = [
        { value: 1e9, suffix: 'Md' }, // Milliards
        { value: 1e6, suffix: 'M' },  // Millions
        { value: 1e3, suffix: 'K' }   // Milliers
    ]

    const formatCompactCurrency = (amount) => {
        const num = Number(amount) || 0
        const absAmount = Math.abs(num)
        let unitIndex = compactUnits.findIndex(u => absAmount >= u.value)
        // 999 950 rounds to "1,000 K": switch to the next unit ("1 M") in that case
        while (unitIndex > 0 && Math.round((absAmount / compactUnits[unitIndex].value) * 10) / 10 >= 1000) {
            unitIndex--
        }
        if (unitIndex === -1 && Math.round(absAmount) >= 1000) unitIndex = compactUnits.length - 1
        const unit = unitIndex === -1 ? null : compactUnits[unitIndex]

        if (!unit) {
            // Less than 1000, use regular format without decimals
            return `${Math.round(num)} ${currencySymbol.value}`
        }

        // 1 decimal max, trailing ".0" dropped, thousands grouped (e.g. "1,250 Md")
        const formatted = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 1
        }).format(num / unit.value)

        return `${formatted} ${unit.suffix} ${currencySymbol.value}`
    }

    /**
     * Format number with US-style separators
     * @param {number} value - The value to format 
     * @param {number} decimals - Precision
     * @returns {string} Formatted string
     */
    const formatDecimal = (value, decimals = 2) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: decimals
        }).format(value || 0)
    }

    /**
     * Parse formatted string back to number
     * @param {string|number} value - The value to parse
     * @returns {number} Clean number
     */
    const parseDecimal = (value) => {
        if (typeof value === 'number') return value
        if (!value) return 0
        // Remove commas and parse
        return parseFloat(String(value).replace(/,/g, '')) || 0
    }

    return {
        currencySymbol,
        loadCurrencySymbol,
        formatCurrency,
        formatDecimal,
        parseDecimal,
        formatCompactCurrency,
        getCurrencySymbol,
        setCurrencySymbol
    }
}
